import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import type { BoardTableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { Overlay } from "../ui/Overlay";
import { avatarSrc } from "../game/avatar";
import { botBare } from "../game/seats";
import { BoardChatButtons } from "../game/RoundSeat";
import { BreakWatch } from "../game/handBreak";
import { BreakNext } from "../game/BreakNext";
import { voiceHold } from "../ui/VoiceButton";
import { synth } from "../game/sfx";
import { breakStreak, newStreak, outSound } from "./streak";
import { BotIcon } from "../ui/BotIcon";
import { BoardView, type BoardHandle, PLAYER_COLORS } from "./BoardView";
import { blockedAt, occupancy, ray } from "./board";
import { type CoopBoard, generateCoop, partsDone, partsOpen } from "./coop";
import { ANIMALS } from "./animals";
import { Confetti, Dock, Intro, ZOOM_TAP, shake, useInsets } from "./RaceView";
import { tailOf } from "./online";
import { clock, clockTenths, fa } from "./fa";

/**
 * میزِ همکاری (کاربر، ۷ آبان؛ انتخابِ کاربر: یک نقشه‌ی بزرگ در حدودِ ۹ تکه، ۲ تا ۴ نفر یک تیم). داور سرور است
 * (Backend/internal/boards/arrows/coop.go) و تخته از همان بذر این‌جا ساخته می‌شود (coop.ts).
 *
 * تکه‌های بسته زیرِ مه‌اند؛ تکه‌ای که تمام شد قابِ طلایی می‌گیرد و تکه‌های کنارش باز می‌شوند — دوربین آرام سراغِ
 * تکه‌های باز می‌رود. قلب‌ها مشترک‌اند: لمسِ فلشِ بسته یکی را از همه می‌برد. هر کس فلش‌های رنگِ خودش را می‌زند، و
 * وقتی در تکه‌های باز چیزی از رنگش نماند، فلش‌های بقیه را («کمک به تیم»).
 *
 * لمس مثلِ میزِ مسابقه بی‌درنگ دیده می‌شود و سرور با out تأییدش می‌کند؛ تأییدنشده بعد از ۱٫۵ ثانیه برمی‌گردد.
 */
const GAP_MS = 200;
const CONFIRM_MS = 1500;

export function CoopTable({ state }: { state: BoardTableState }) {
  const h = state.board;
  const board = useMemo<CoopBoard | null>(
    // نقشِ ۱۰۰ به بالا نقشه‌ی جانور است (game.go، AnimalCode)، −۱ فرش
    () => (h.length >= 7 && h[6] === 1 ? generateCoop(h[4]!, h[1]!, { arrows: h[2]!, len: h[3]! }, h[0]! >= 100 ? h[0]! - 100 : -1) : null),
    // تخته فقط با سرِ نما عوض می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [h.join(",")],
  );
  const players = h[1] ?? state.seats.length;
  const me = state.watching ? -1 : state.me;
  /** نقشه‌ی جانور: نامش سرِ «برو» یک لحظه روی تخته می‌آید (در شمارش تخته هنوز نیامده). */
  const beast = h[0] !== undefined && h[0] >= 100 ? ANIMALS[h[0] - 100] : undefined;
  const tail = tailOf(state.extra, players);
  const phase = state.over ? 2 : state.phase;
  const brk = useMemo(() => new BreakWatch(), []);
  useEffect(() => { brk.update(state.brk, state.seats, state.me, state.watching); }, [brk, state]);

  const boardRef = useRef<BoardHandle>(null);
  const stageEl = useRef<HTMLDivElement>(null);
  const hud = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLDivElement>(null);
  const inset = useInsets(hud, dock);
  const pending = useRef(new Map<number, number>());
  const lastTap = useRef(0);
  const [hit, setHit] = useState(0);
  const [, setVer] = useState(0);
  const timerEl = useRef<HTMLSpanElement>(null);
  const goAt = useRef(0);
  goAt.current = performance.now() - (state.extra[0] ?? 0);

  const server = new Set(state.hand);
  const now = performance.now();
  for (const [id, at] of pending.current) if (server.has(id) || now - at > CONFIRM_MS) pending.current.delete(id);
  const gone = board ? board.arrows.map((a) => server.has(a.id) || pending.current.has(a.id)) : [];
  const done = board ? partsDone(board, gone) : [];
  const open = board ? partsOpen(board, done) : [];
  const alive = board ? board.arrows.map((a) => open[board.partOf[a.id]!]! && !gone[a.id]) : [];
  const mineLeft = board && me >= 0 ? board.arrows.filter((a) => alive[a.id] && a.owner === me).length : 0;
  const helping = me >= 0 && phase === 1 && mineLeft === 0 && alive.some(Boolean);
  // نقشه‌ی جانور: کاشی‌ای که جانور به آن نرسیده بی‌فلش است — نه کشیده می‌شود، نه شمرده (از اول «تمام» است)
  const filled = useMemo(() => (board ? board.parts.map((_, i) => board.partOf.includes(i)) : []), [board]);
  const doneCount = done.filter((d, i) => d && filled[i]).length;
  const partCount = filled.filter(Boolean).length;

  useEffect(() => { pending.current.clear(); }, [board]);

  const parts = useMemo(() => board?.parts.flatMap((p, i) => (filled[i] ? [{
    x: p.x, y: p.y, w: p.w, h: p.h, state: done[i] ? "done" as const : open[i] ? "open" as const : "locked" as const,
  }] : [])),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [board, filled, done.join(), open.join()]);
  // دوربین: تکه‌های باز و هنوز ناتمام؛ همه که تمام شد، کلِ نقشه
  const follow = useMemo(() => {
    if (!board) return undefined;
    const live = board.parts.filter((_, i) => filled[i] && open[i] && !done[i]);
    const ps = live.length ? live : board.parts;
    const x0 = Math.min(...ps.map((p) => p.x)), y0 = Math.min(...ps.map((p) => p.y));
    const x1 = Math.max(...ps.map((p) => p.x + p.w)), y1 = Math.max(...ps.map((p) => p.y + p.h));
    return { x: x0 - 0.5, y: y0 - 0.5, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, done.join(), open.join()]);
  const focus = useMemo(() => {
    const c = board?.parts[4];
    return c && { x: c.x - 0.5, y: c.y - 0.5, w: c.w + 1, h: c.h + 1 };
  }, [board]);

  useEffect(() => client.on((env) => {
    if (env.msg.case !== "boardEvent") return;
    const e = env.msg.value;
    if (e.kind === "go") synth("chime");
    if (e.kind === "open") synth("chime");
    if (e.kind === "out" && !pending.current.has(e.a)) boardRef.current?.fly(e.a, e.b);
    if (e.kind === "blocked" && e.seat !== me) {
      boardRef.current?.bump(e.a, e.b);
      setHit((k) => k + 1); // قلبِ همه بود
    }
  }), [me]);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      if (timerEl.current && phase === 1) timerEl.current.textContent = clock(Math.max(0, performance.now() - goAt.current));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    voiceHold.value = phase === 0 || phase === 1;
    return () => { voiceHold.value = false; };
  }, [phase]);

  /** پشتِ‌همِ فلش‌های خودم (streak.ts). */
  const streak = useRef(newStreak());
  const onTap = (id: number) => {
    if (!board || me < 0 || phase !== 1) return;
    const t = performance.now();
    if (t - lastTap.current < GAP_MS) return;
    lastTap.current = t;
    const a = board.arrows[id]!;
    const block = blockedAt(board, occupancy(board, alive), a);
    client.boardMove(2, id);
    if (block > 0) {
      boardRef.current?.bump(id, block);
      synth("stamp");
      breakStreak(streak.current);
      shake(stageEl.current);
      setHit((k) => k + 1);
      try { navigator.vibrate?.(tail.hearts <= 1 ? [60, 40, 90] : 70); } catch { /* بی لرزش هم کار می‌کند */ }
      return;
    }
    pending.current.set(id, t);
    boardRef.current?.fly(id, ray(board, a).length);
    outSound(streak.current, t);
    setVer((v) => v + 1);
  };

  const people = state.seats.slice(0, players).map((s, i) => ({
    name: i === me ? "تو" : botBare(s.name || "یار", s.bot),
    face: avatarSrc(s.avatar),
    bot: s.bot,
    color: PLAYER_COLORS[i]!,
    left: board ? board.arrows.filter((a) => a.owner === i && !gone[a.id]).length : 0,
  }));
  const left = Math.ceil(-(state.extra[0] ?? 0) / 1000);
  const mapNo = Math.min(tail.rounds, tail.round + (phase === 3 && tail.result === 1 ? 0 : 1));

  return (
    <div className="table ar-race online coop">
      <div className="ar-stage" ref={stageEl} onAnimationEnd={(e) => { if (e.target === stageEl.current) stageEl.current.classList.remove("shake"); }}>
        {board && (
          <BoardView ref={boardRef} board={board} alive={alive} onTap={onTap} inset={inset}
            own={me < 0 || helping ? undefined : me} mates={me >= 0 && !helping ? people.map((_, i) => i).filter((i) => i !== me) : undefined}
            focus={focus} follow={follow} parts={parts} locked={phase !== 1 || me < 0} zoomTap={beast ? ZOOM_TAP : undefined}
            cleared={gone} />
        )}
      </div>
      {hit > 0 && <div className="ar-hurt" key={hit} aria-hidden />}

      <div className="ar-hud" ref={hud}>
        <div className="ar-top solo">
          <div className="ar-clock"><span ref={timerEl}>{clock(0)}</span></div>
          <span className="ar-roundchip">نقشه‌ی {fa(mapNo)} از {fa(tail.rounds)}</span>
        </div>
        <div className="ar-solobar">
          <span className={"ar-hearts" + (tail.maxHearts > 6 ? " many" : "")} aria-label={`${fa(tail.hearts)} قلب`}>
            {Array.from({ length: tail.maxHearts }, (_, k) => (
              <svg key={k} viewBox="0 0 24 24" className={k < tail.hearts ? "on" : "off"} aria-hidden>
                <path d="M12 20.8 3.6 12.4a5 5 0 0 1 7.1-7.1l1.3 1.3 1.3-1.3a5 5 0 0 1 7.1 7.1z" />
              </svg>
            ))}
          </span>
          <span className="ar-coop-team">
            {people.map((p, i) => (
              <span key={i} className={"ar-coop-who" + (i === me ? " me" : "")} style={{ "--rc": p.color } as CSSProperties}>
                <img src={p.face} alt="" />
                {p.bot && <span className="ar-botbadge"><BotIcon size={9} /></span>}
                <small>{fa(p.left)}</small>
              </span>
            ))}
          </span>
          <span className="ar-solo-left"><b>{fa(doneCount)}</b><small>از {fa(partCount)} تکه</small></span>
          <i className="ar-solo-bar" style={{ "--p": `${(doneCount / Math.max(1, partCount)) * 100}` } as CSSProperties}><i /></i>
        </div>
        {helping && (
          <div className="ar-help gold">
            <svg viewBox="0 0 24 24" aria-hidden><path d="M7 11V6.5a1.5 1.5 0 0 1 3 0V11m0-1.5v-5a1.5 1.5 0 0 1 3 0v5m0 0v-3a1.5 1.5 0 0 1 3 0V12m0-2.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-4.6-2.2L4.6 15.6a1.6 1.6 0 0 1 2.4-2.1L7 13.5" /></svg>
            کمک به تیم
          </div>
        )}
      </div>

      <Dock refEl={dock} board={boardRef} />
      {!state.watching && <div className="ar-chat"><BoardChatButtons onReact={(k) => client.sendCanned(k)} /></div>}

      {phase === 0 && <Intro head="همکاری" name={`نقشه‌ی ${fa(tail.round + 1)} از ${fa(tail.rounds)}`} />}
      {phase === 1 && beast && <div className="ar-beastname" key={beast.id}>{beast.name}</div>}
      {phase === 0 && left > 0 && <div className="ar-count" key={left}>{fa(left)}</div>}

      {phase === 3 && <MapOver won={tail.result === 1} hearts={tail.hearts} maxHearts={tail.maxHearts} at={state.extra[1] ?? -1}
        left={gone.filter((g) => !g).length} mapNo={mapNo} name={beast?.name} watch={brk} />}
      {phase === 2 && <CoopOver maps={tail.rounds} stars={tail.stars} fails={tail.fails} me={me} />}
    </div>
  );
}

/** ستاره‌ی نقشه از قلب‌های مانده — همان Stars ِ coop.go. */
export const coopStars = (hearts: number, max: number) => (hearts >= max ? 3 : 2 * hearts >= max ? 2 : 1);

/** پنلِ پایانِ یک نقشه: ستاره‌ها و «نقشه‌ی بعد»، یا قلبِ شکسته و «دوباره» — با «ادامه» ِ همه. */
function MapOver({ won, hearts, maxHearts, at, left, mapNo, name, watch }: {
  won: boolean; hearts: number; maxHearts: number; at: number; left: number; mapNo: number; name?: string; watch: BreakWatch;
}) {
  const stars = won ? coopStars(hearts, maxHearts) : 0;
  const title = won ? (stars === 3 ? "بی‌نقص!" : "نقشه کامل شد!") : hearts > 0 ? "وقت تمام شد" : "قلب‌ها تمام شد";
  return (
    <div className="ar-veil">
      {won && <Confetti />}
      <div className={"ar-result solo" + (won ? " win" : "")}>
        {won ? (
          <div className="ar-stars" aria-label={`${fa(stars)} ستاره`}>
            {[1, 2, 3].map((k) => (
              <svg key={k} viewBox="0 0 24 24" className={k <= stars ? "on" : "off"} style={{ animationDelay: `${0.15 + k * 0.18}s` }} aria-hidden>
                <path d="M12 2.5l2.9 5.9 6.6.9-4.8 4.6 1.2 6.5-5.9-3.1-5.9 3.1 1.2-6.5L2.5 9.3l6.6-.9z" />
              </svg>
            ))}
          </div>
        ) : (
          <svg className="ar-broken" viewBox="0 0 24 24" aria-hidden>
            <path d="M12 20.8 3.6 12.4a5 5 0 0 1 7.1-7.1l1.3 1.3 1.3-1.3a5 5 0 0 1 7.1 7.1z" />
            <path d="M12 6.6l-1.6 3.6 2.6 1.8-2.2 4" className="crack" />
          </svg>
        )}
        <div className="ar-title">
          <b>{title}</b>
          <span>
            نقشه‌ی {fa(mapNo)}
            {name && <> · {name}</>}
            {won && at >= 0 && <> · <em>{clockTenths(at)}</em></>}
            {!won && <> · <em>{fa(left)} فلش مانده بود</em></>}
          </span>
        </div>
        <div className="ar-actions single">
          <BreakNext watch={watch} label={won ? "نقشه‌ی بعد" : "دوباره"} onNext={() => watch.press()} />
        </div>
      </div>
    </div>
  );
}

/** پایانِ میزِ همکاری: همه با هم بردند. */
function CoopOver({ maps, stars, fails, me }: { maps: number; stars: number; fails: number; me: number }) {
  return (
    <Overlay label="پایان بازی" card="win">
      <Confetti />
      <div className="vt">همه‌ی نقشه‌ها را با هم باز کردید!</div>
      <div className="ar-coop-sum">
        <span><b>{fa(maps)}</b><small>نقشه</small></span>
        <span className="stars"><b>{fa(stars)}</b><small>ستاره از {fa(maps * 3)}</small></span>
        {fails > 0 && <span><b>{fa(fails)}</b><small>بار از نو</small></span>}
      </div>
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {me < 0 ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}
